<template>
    <div class="text-center">
        <div class="success-badge mb-2">
            <v-icon color="white" size="40">mdi-check-bold</v-icon>
        </div>
        <h1 :class="this.$vuetify.breakpoint.smAndDown ? 'text-h5' : 'secondary--text text-h2'" class="mt-2">
            Congratulations!
        </h1>
        <h3 class="secondary--text text-h5 font-weight-regular confirmation-subtext mx-auto">
            Your Message IQ plan and account have been confirmed. You are on your way to a faster and
            more effective way to communicate with your prospects and customers.
        </h3>

        <v-row justify="center" class="mt-6">
            <v-col cols="12" sm="10" md="9">
                <div class="content-card text-left">
                    <h4 class="text-overline section-eyebrow mb-4">What happens next</h4>

                    <p>
                        You should have already received a welcome email from us. Please verify that you have
                        it and reach out to <a href="mailto:help@messageiq.io">help@messageiq.io</a> if you don't.
                    </p>
                    <p>
                        Text messaging is regulated. Before you can send your first message, the carriers and
                        industry regulators need to know two things: who you are, and what you plan to send.
                    </p>
                    <p class="font-weight-bold mb-6">That takes two applications.</p>

                    <div class="application-step d-flex align-start">
                        <div class="application-icon">
                            <v-icon color="white" size="22">mdi-domain</v-icon>
                        </div>
                        <div class="pb-6 application-body">
                            <div class="text-subtitle-1 font-weight-bold">1. Brand application: who you are</div>
                            <div class="secondary--text">
                                Basic details about your company. We submit this to The Campaign Registry (TCR)
                                on your behalf. TCR generally responds within one business day.
                            </div>
                        </div>
                    </div>
                    <div class="application-step d-flex align-start application-step-last">
                        <div class="application-icon">
                            <v-icon color="white" size="22">mdi-message-bulleted</v-icon>
                        </div>
                        <div class="application-body">
                            <div class="text-subtitle-1 font-weight-bold">2. Campaign application: what you'll send</div>
                            <div class="secondary--text">
                                The kinds of messages you plan to send, how people opt in, and what your website
                                needs to say about it.
                            </div>
                        </div>
                    </div>
                </div>
            </v-col>
        </v-row>

        <v-row justify="center" class="mt-6">
            <v-col cols="12" sm="10" md="9">
                <div class="cta-card">
                    <h4 class="text-overline cta-eyebrow mb-2">Next step</h4>
                    <p class="white--text mb-6 cta-copy">
                        Complete your brand application. Everything else waits on it, so the sooner it's in,
                        the sooner you can begin messaging.
                    </p>
                    <v-btn
                        x-large color="white" class="primary--text font-weight-bold"
                        :href="brandApplicationUrl" target="_blank" rel="noopener"
                    >
                        Brand Application
                        <v-icon right>mdi-open-in-new</v-icon>
                    </v-btn>
                </div>
            </v-col>
        </v-row>

        <v-row justify="center" class="mt-6">
            <v-col cols="12" sm="10" md="9">
                <div class="content-card text-left">
                    <h4 class="text-overline section-eyebrow mb-3">What happens on our side</h4>
                    <p class="mb-6">
                        Once we submit your brand, you'll get an email from us. It covers any updates your
                        website needs and walks you through the campaign application.
                    </p>

                    <h4 class="text-overline section-eyebrow mb-3">How long it takes</h4>
                    <div class="d-flex align-center">
                        <div class="timing-badge mr-4">
                            <v-icon color="white" size="22">mdi-clock-outline</v-icon>
                            <span class="white--text font-weight-bold">~2 weeks</span>
                        </div>
                        <span class="secondary--text">Plan on about two weeks for regulatory approval.</span>
                    </div>
                </div>
            </v-col>
        </v-row>

        <v-footer :app="this.$vuetify.breakpoint.smAndDown" padless class="px-0 bg-transparent">
            <v-container fluid class="pa-0">
                <v-row no-gutters>
                    <v-col cols="12">
                        <v-card-actions class="d-flex justify-center px-0">
                            <v-btn text color="secondary" @click="goToLogin">
                                Log In to Message IQ
                            </v-btn>
                        </v-card-actions>
                    </v-col>
                </v-row>
            </v-container>
        </v-footer>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';

// Where a signed-in Message IQ user lands after login. Matches the mapping used in
// src/guards/productRegGuard.js (goToConfigurationPage) for the MessageIQ product,
// since this signup flow is Message IQ specific.
const POST_LOGIN_LANDING_ROUTE = '/conversations';

// HubSpot-hosted brand application form. Pre-fills the lead's email via the
// form's own "email" query param.
const BRAND_APPLICATION_BASE_URL = 'https://share.hsforms.com/1IE7puZGgRuGkUm0uYmmirA1tcm2';

export default {
    name: "Confirmation",
    computed: {
        ...mapGetters('messageIqSignup', { email: 'getEmail' }),
        brandApplicationUrl() {
            const params = new URLSearchParams({ email: this.email || '' });
            return `${BRAND_APPLICATION_BASE_URL}?${params.toString()}`;
        }
    },
    methods: {
        goToLogin() {
            this.$auth.loginWithRedirect({
                login_hint: this.email,
                appState: { targetUrl: POST_LOGIN_LANDING_ROUTE }
            });
        }
    }
};
</script>

<style lang="scss" scoped>
.success-badge {
    width: 84px;
    height: 84px;
    margin: 0 auto;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background-image: linear-gradient(135deg, #0cc27e 0%, #03b374 100%);
    box-shadow: 0 10px 24px rgba(12, 194, 126, 0.35);
}

.confirmation-subtext {
    max-width: 640px;
}

.content-card {
    border-radius: 20px;
    padding: 32px 36px;
    background-color: #fff;
    box-shadow: 0 12px 32px rgba(34, 45, 96, 0.12);

    p {
        margin-bottom: 16px;
    }
}

.section-eyebrow {
    letter-spacing: 1.5px;
    color: #ed0c87;
    font-weight: 700;
    display: block;
}

.application-step {
    position: relative;
}

.application-icon {
    position: relative;
    z-index: 1;
    flex: 0 0 42px;
    width: 42px;
    height: 42px;
    border-radius: 50%;
    margin-right: 20px;
    background-image: linear-gradient(135deg, #ed0c87 0%, #415a99 100%);
    box-shadow: 0 4px 10px rgba(237, 12, 135, 0.3);
    display: flex;
    align-items: center;
    justify-content: center;
}

// Connects the brand-application icon down to the campaign-application icon.
.application-step:not(.application-step-last) {
    &::after {
        content: "";
        position: absolute;
        top: 42px;
        bottom: 0;
        left: 20px;
        width: 2px;
        background-color: rgba(65, 90, 153, 0.15);
    }
}

.application-body {
    padding-top: 8px;
}

.cta-card {
    border-radius: 20px;
    padding: 36px 40px;
    text-align: center;
    background-image: linear-gradient(135deg, #ed0c87 0%, #415a99 100%);
    box-shadow: 0 16px 36px rgba(237, 12, 135, 0.3);
}

.cta-eyebrow {
    letter-spacing: 1.5px;
    color: #fff;
    font-weight: 700;
    opacity: 0.85;
}

.cta-copy {
    max-width: 520px;
    margin-left: auto;
    margin-right: auto;
}

.timing-badge {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    border-radius: 999px;
    background-image: linear-gradient(135deg, #ed0c87 0%, #415a99 100%);
    box-shadow: 0 4px 10px rgba(237, 12, 135, 0.3);
}
</style>
